import { useState, useEffect } from 'react';
import { getRallyBonusLog } from '../../../services/ghostRallyService';

const BONUS_EMOJI = {
  time_cut: '⚡',
  big_time_cut: '🚀',
  shield: '🛡',
  add_1_min: '🎯',
  add_2_min: '💥',
  no_effect: '🎲',
};

/**
 * Chronological audit log of every bonus awarded in a rally. Self-polls
 * every 5s while mounted; pass `polling={false}` to stop. Designed to
 * drop into both the admin dashboard panel and the driver leaderboard
 * modal — neutral styling adapts to either.
 */
export default function RallyBonusLog({ rallyId, currentUserId, polling = true, dark = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      const { data } = await getRallyBonusLog(rallyId);
      if (!alive) return;
      setEntries(data || []);
      setLoading(false);
    }
    load();
    if (!polling) return () => { alive = false; };
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [rallyId, polling]);

  if (loading) {
    return <p style={{ ...textMuted(dark), padding: 12, fontSize: '0.8125rem' }}>Loading…</p>;
  }
  if (entries.length === 0) {
    return (
      <p style={{ ...textMuted(dark), padding: 12, fontSize: '0.8125rem', textAlign: 'center' }}>
        No bonuses yet — the log fills as drivers claim checkpoints.
      </p>
    );
  }

  return (
    <div>
      {entries.map(e => (
        <Entry key={e.id} entry={e} isMe={e.user_id === currentUserId} amTarget={e.target_user_id === currentUserId} dark={dark} />
      ))}
    </div>
  );
}

function Entry({ entry, isMe, amTarget, dark }) {
  const time = new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const emoji = BONUS_EMOJI[entry.bonus_type] || '✨';
  const isAttack = entry.seconds_delta > 0 && entry.target_user_id;
  const wasBlocked = entry.was_blocked_by_shield;

  // Tint: attack on me = warning, my bonus on others = primary, my self bonus = green.
  const accent = amTarget && isAttack && !wasBlocked
    ? '#fbbf24'
    : isMe && entry.seconds_delta < 0
      ? '#22c55e'
      : isMe && isAttack
        ? '#a78bfa'
        : 'transparent';

  return (
    <div style={{
      padding: '10px 12px',
      borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'var(--border)'}`,
      background: (isMe || amTarget) ? (dark ? 'rgba(255,255,255,0.04)' : 'rgba(167,139,250,0.06)') : 'transparent',
      borderLeft: `3px solid ${accent}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: '0.8125rem', color: dark ? '#fff' : 'var(--text-primary)' }}>
        <span style={{ fontSize: '1rem' }}>{emoji}</span>
        <span style={{ fontWeight: 700 }}>{entry.user_name}{isMe ? ' (you)' : ''}</span>
        <span style={textMuted(dark)}>·</span>
        <span style={textMuted(dark)}>{entry.checkpoint_name || 'Checkpoint'}</span>
      </div>

      <div style={{ marginTop: 4, marginLeft: 24, fontSize: '0.8125rem', color: dark ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)' }}>
        {entry.description}
        {isAttack && (
          <>
            <span style={textMuted(dark)}> on </span>
            <span style={{ fontWeight: 600 }}>{entry.target_name || 'someone'}{amTarget ? ' (you)' : ''}</span>
            {wasBlocked && (
              <span style={{ marginLeft: 6, fontSize: '0.6875rem', fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(34,197,94,0.18)', color: '#22c55e' }}>
                BLOCKED 🛡
              </span>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: 4, marginLeft: 24, fontSize: '0.6875rem', ...textMuted(dark), fontVariantNumeric: 'tabular-nums' }}>
        {time}
      </div>
    </div>
  );
}

function textMuted(dark) {
  return { color: dark ? 'rgba(255,255,255,0.55)' : 'var(--text-muted)' };
}
